import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import PageLayout from "components/layout/page-layout";
import { useSharedState } from "state";
import compactNumber from "helpers/compact-number";
import { BudgetReport, CalculatedFormulaValues, ExcelPayload, Report } from "types/report";
import { AgGridReact } from "ag-grid-react";
import { TreeDataModule } from "ag-grid-enterprise";
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  ColDef,
  ColumnAutoSizeModule,
  ModuleRegistry,
  RowAutoHeightModule,
  ValidationModule,
  ValueFormatterParams,
} from "ag-grid-community";
import { IconInfoCircle } from "@tabler/icons-react";
import { downloadBudgetReport, generateBudgetReport, saveForecast } from "api/budget_request";
import moment from "moment";

ModuleRegistry.registerModules([
  AllCommunityModule,
  TreeDataModule,
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  RowAutoHeightModule,
  ValidationModule,
]);

export default function Reports() {
  const gridRef = useRef<AgGridReact>(null);
  const {
    isAdmin,
    levels,
    getLevelsData,
    report,
    setReport,
    reportDetails,
    setReportDetails,
    reportType,
    setReportType,
    excelName,
    setExcelName,
    budgetColumns,
    getBudgetColumnsData,
    budgetForecasts,
    getBudgetForecastsData,
    notify,
  } = useSharedState();

  const navigate = useNavigate();

  const [forecastValues, setForecastValues] = useState<number[]>([]);

  const [isForecastChanged, setForecastChanged] = useState<boolean>(false);
  const [isFormLoading, setFormLoading] = useState<boolean>(false);

  const columnHeader = useMemo(() => {
    return (column: string) =>
      column.includes("lvl")
        ? `Уровень ${column.slice(3)}`
        : column === "forecast_value"
        ? "Прогноз"
        : budgetColumns.find((c) => c.value === column)?.label ||
          budgetForecasts.find((forecast) => forecast.value === column)?.label ||
          "";
  }, [report.columns]);

  useEffect(() => {
    getBudgetColumnsData();
    getBudgetForecastsData();
  }, []);

  useEffect(() => {
    report.report.length && setForecastValues(report.report.map((row) => row.forecast_value));
    getLevelsData();
  }, [report]);

  useEffect(() => {
    if (forecastValues.every((el, index) => el === report.report[index].forecast_value)) setForecastChanged(false);
  }, [forecastValues]);

  const numberProps = useMemo(() => {
    return (row: Report | CalculatedFormulaValues) => {
      const excludeColumns = report.columns.slice(
        0,
        Math.min(
          report.columns.indexOf("quantity") >= 0 ? report.columns.indexOf("quantity") : Infinity,
          report.columns.indexOf("row_total") >= 0 ? report.columns.indexOf("row_total") : Infinity,
          report.columns.indexOf("mon_1") >= 0 ? report.columns.indexOf("mon_1") : Infinity
        )
      );

      return Object.fromEntries(Object.entries(row).filter(([key]) => !excludeColumns.includes(key)));
    };
  }, [report]);

  const reportTableRows = useMemo(
    () => [
      ...report.report.map((row, rowIndex) => ({
        ...numberProps(row),
        vat: row.vat === true ? "Да" : row.vat === false ? "Нет" : undefined,
        id: rowIndex,
        path: [
          ...report.columns
            .slice(
              0,
              Math.min(
                report.columns.indexOf("quantity") >= 0 ? report.columns.indexOf("quantity") : Infinity,
                report.columns.indexOf("row_total") >= 0 ? report.columns.indexOf("row_total") : Infinity,
                report.columns.indexOf("mon_1") >= 0 ? report.columns.indexOf("mon_1") : Infinity
              )
            )
            // @ts-ignore
            .map((col) => (col === "vat" ? (row[col] ? "НДС: Да" : "НДС: Нет") : row[col])),
        ],
      })),
      ...report.calculated_formulas.map((formula, forIndex) => ({
        ...numberProps(formula.value),
        // @ts-ignore
        vat: formula.vat === true ? "Да" : formula.vat === false ? "Нет" : undefined,
        id: `formula_${forIndex}`,
        path: [formula.formula_name],
      })),
    ],
    [report, numberProps]
  );

  const onExcelDownload = async () => {
    try {
      setFormLoading(true);

      const payload: ExcelPayload = {
        file_name: report.file_name,
        file_path: report.file_path,
      };
      const res: any = await downloadBudgetReport(payload);

      const fileName = `${excelName} ${moment().format("DD.MM.YYYY HH:mm")}.xlsx`;
      const blob = new Blob([res], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName.replaceAll(" ", "_");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      notify("Скачивание завершено", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении файла");
    } finally {
      setFormLoading(false);
    }
  };

  const onSaveForecast = async () => {
    try {
      setFormLoading(true);

      const payload = {
        year: reportDetails?.year,
        company_id: reportDetails?.company_id[0],
        list_budget_types: report.report.map((row, rowIndex) => {
          const updatedRow = { ...row };
          Object.keys(updatedRow).forEach((key) => {
            if (key.match(/^lvl\d+$/) || key === "parent_budget_type") {
              const newKey = key === "parent_budget_type" ? "lvl_0" : key.replace(/(\d+)/, "_$1");
              const matchingLevel = levels.find((level) => level.budget_type === (row as any)[key]);
              (updatedRow as any)[newKey] = matchingLevel ? matchingLevel.budget_type_id : null;
              delete (updatedRow as any)[key];
            }
          });

          const filteredRow = Object.keys(updatedRow)
            .filter((key) => key.startsWith("lvl_"))
            .reduce((obj, key) => ({ ...obj, [key]: (updatedRow as any)[key] }), {});

          return { ...filteredRow, forecast_value: forecastValues[rowIndex] };
        }),
      };
      await saveForecast(payload);

      const res: BudgetReport = await generateBudgetReport(reportDetails);
      setReport(res);
      setReportDetails(reportDetails);

      notify("Прогноз сохранён", "success");
      setForecastValues([]);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при сохранении прогноза");
    } finally {
      setFormLoading(false);
    }
  };

  const reportTableColumns: ColDef[] = report.columns
    .map(
      (column) =>
        report.report.length &&
        report.report.map((row) => ({ ...numberProps(row) })).some((item) => item[column] !== undefined) && {
          field: column,
          headerName: columnHeader(column),
          aggFunc:
            column.includes("quantity") ||
            column.includes("mon") ||
            column.includes("row_total") ||
            budgetForecasts.find((forecast) => forecast.value === column)?.value
              ? "sum"
              : undefined,
          editable: isAdmin && column === "forecast_value",
          cellEditor: isAdmin && column === "forecast_value" ? "agNumberCellEditor" : undefined,
          valueFormatter: (params: ValueFormatterParams) =>
            column.includes("quantity") ||
            column.includes("mon") ||
            column.includes("row_total") ||
            budgetForecasts.find((forecast) => forecast.value === column)?.value
              ? compactNumber(params.value || "") || 0
              : params.value,
        }
    )
    .filter(Boolean) as ColDef[];

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      minWidth: 150,
      resizable: true,
      autoHeight: true,
      editable: false,
    };
  }, []);

  const autoSizeStrategy = useMemo(() => {
    return {
      type: "fitCellContents" as const,
    };
  }, []);

  const autoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      width: 400,
      headerName: "Наименование",
      cellRendererParams: {
        suppressCount: true,
      },
      valueFormatter: (params: ValueFormatterParams) => params.value,
    };
  }, []);

  const getDataPath = useCallback((data: any) => data.path, []);

  const onGridReady = useCallback(() => {
    gridRef.current?.api?.sizeColumnsToFit({
      defaultMaxWidth: 150,
    });
  }, []);

  const onCellValueChanged = useCallback((event: any) => {
    if (event.column.colId === "forecast_value") {
      const index = event.data.id;
      const value = event.newValue;
      setForecastValues((prev) => {
        const newValues = [...prev];
        newValues[index] = +value;
        return newValues;
      });
      setForecastChanged(true);
    }
  }, []);

  const reportTable = (
    <div style={{ width: "100%", height: "calc(100dvh - 108px)" }}>
      <AgGridReact
        ref={gridRef}
        rowData={reportTableRows}
        columnDefs={reportTableColumns}
        defaultColDef={defaultColDef}
        autoSizeStrategy={autoSizeStrategy}
        autoGroupColumnDef={autoGroupColumnDef}
        treeData={true}
        getDataPath={getDataPath}
        onGridReady={onGridReady}
        onCellValueChanged={onCellValueChanged}
      />
    </div>
  );

  const tabularReportTableRows = useMemo(
    () => [
      ...report.report.map((row, rowIndex) => ({
        ...row,
        vat: row.vat === true ? "Да" : row.vat === false ? "Нет" : undefined,
        id: rowIndex,
      })),
      ...report.calculated_formulas.map((formula, forIndex) => ({
        ...numberProps(formula.value),
        // @ts-ignore
        vat: formula.vat === true ? "Да" : formula.vat === false ? "Нет" : undefined,
        id: `formula_${forIndex}`,
        [report.columns[0]]: formula.formula_name,
      })),
    ],
    [report, numberProps]
  );

  const tabularReportTableColumns: ColDef[] = report.columns
    .map(
      (column) =>
        report.report.length &&
        // @ts-ignore - We know these fields exist on the Report type
        report.report.some((item) => item[column] !== undefined) && {
          field: column,
          headerName: columnHeader(column),
          aggFunc:
            column.includes("quantity") ||
            column.includes("mon") ||
            column.includes("row_total") ||
            budgetForecasts.find((forecast) => forecast.value === column)?.value
              ? "sum"
              : undefined,
          editable: isAdmin && column === "forecast_value",
          cellEditor: isAdmin && column === "forecast_value" ? "agNumberCellEditor" : undefined,
          valueFormatter: (params: ValueFormatterParams) =>
            column.includes("quantity") ||
            column.includes("mon") ||
            column.includes("row_total") ||
            budgetForecasts.find((forecast) => forecast.value === column)?.value
              ? compactNumber(params.value || "") || 0
              : params.value,
        }
    )
    .filter(Boolean) as ColDef[];

  const tabularReportTable = (
    <div style={{ width: "100%", height: "calc(100dvh - 108px)" }}>
      <AgGridReact
        ref={gridRef}
        rowData={tabularReportTableRows}
        columnDefs={tabularReportTableColumns}
        defaultColDef={defaultColDef}
        autoSizeStrategy={autoSizeStrategy}
        treeData={false}
        onGridReady={onGridReady}
        onCellValueChanged={onCellValueChanged}
      />
    </div>
  );

  const tablePlaceholder = (
    <div className="alert alert-info" role="alert">
      <div className="d-flex">
        <div>
          <IconInfoCircle className="icon alert-icon" />
        </div>
        <div>
          <h4 className="alert-title">Данных пока нет</h4>
          <div className="text-secondary">
            Используйте кнопку "Новый отчёт", чтобы создать новый отчёт и начать работу с данными.
          </div>
        </div>
      </div>
    </div>
  );

  const tableNode =
    report.report.length && reportType === "hierarchical"
      ? reportTable
      : report.report.length && reportType === "tabular"
      ? tabularReportTable
      : tablePlaceholder;

  const additionalButtons = (
    <div className="d-flex flex-wrap gap-2 w-full md-w-fit">
      {report.columns.includes("forecast_value") && (
        <button
          className="btn btn-primary grow gap-2"
          onClick={onSaveForecast}
          disabled={isFormLoading || !isForecastChanged}
        >
          Сохранить прогноз
          {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
        </button>
      )}

      <button className="btn btn-primary grow gap-2" onClick={onExcelDownload} disabled={isFormLoading}>
        Скачать Excel
        {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
      </button>
    </div>
  );

  return (
    <>
      <PageLayout
        children={tableNode}
        header={excelName ? `Отчёты: ${excelName}` : "Отчёты"}
        search={false}
        filters={false}
        buttonText={"Новый отчёт"}
        buttonAction={() => navigate("create")}
        additionalButton={report.report.length ? additionalButtons : undefined}
      />
      <Outlet />
    </>
  );
}
